// Agent "My holds" rows (ID-119), shared by the units endpoint (per project) and /deals (all holds). Pure.
import type { HoldDto } from '~/server/utils/exclusives/holds/holdSnapshot';
import type { DealPrePopulation } from '~/store/dealRegistration';

export type ExclusiveHoldRow = {
  holdId: string;
  status: string;
  catalogueProjectId: number;
  catalogueUnitId: number;
  unitNumber: string;
  unitType: string | null;
  projectName: string | null;
  developerName: string | null;
  priceAed: number | null;
  clientName: string;
  clientPhone: string | null;
  expiresAt: string;
  dealRegistrationId: string | null;
  // Phone-verified viewers only (hold snapshot: price × commission % × share %).
  agentEarningsAed?: number | null;
};

// Only live holds with a server expiry are shown; a PENDING hold (no expiry yet) appears on the next poll.
export const holdRowFromDto = (dto: HoldDto): ExclusiveHoldRow | null => {
  if (dto.status !== 'ACTIVE' || !dto.expiresAt) return null;
  const row: ExclusiveHoldRow = {
    holdId: dto.id,
    status: dto.status,
    catalogueProjectId: dto.catalogueProjectId,
    catalogueUnitId: dto.catalogueUnitId,
    unitNumber: dto.unitNumber ?? String(dto.catalogueUnitId),
    unitType: dto.unitType,
    projectName: dto.projectName,
    developerName: dto.developerName,
    priceAed: dto.priceAed,
    clientName: dto.clientName,
    clientPhone: dto.clientPhone,
    expiresAt: dto.expiresAt,
    dealRegistrationId: dto.dealRegistrationId,
  };
  if ('agentEarningsAed' in dto) row.agentEarningsAed = dto.agentEarningsAed ?? null;
  return row;
};

export const holdRowsFromDtos = (dtos: readonly HoldDto[]): ExclusiveHoldRow[] =>
  dtos.flatMap((dto) => holdRowFromDto(dto) ?? []);

// "Continue to deal": the self-close registration flow, prefilled from the hold snapshot (server re-snapshots on POST).
export const prePopulationFromHold = (row: ExclusiveHoldRow): DealPrePopulation => ({
  projectId: String(row.catalogueProjectId),
  projectName: row.projectName ?? '',
  developerName: row.developerName,
  unitTypeKey: null,
  unitTypeLabel: row.unitType,
  unitPrice: row.priceAed,
  currency: 'AED',
  exclusiveHoldId: row.holdId,
  catalogueProjectId: row.catalogueProjectId,
  unitNumber: row.unitNumber,
  unitType: row.unitType,
  clientName: row.clientName,
  clientPhone: row.clientPhone,
  earningsAed: row.agentEarningsAed ?? null,
  readOnlyPrice: true,
});
